import React from "react";
import { X, Send, Mail, Instagram, MessageCircle, Link2 } from "lucide-react";
import { assetUrl } from "@/lib/branding";

// Contact card shown when there is no public self-registration. Only filled fields render.
export function JoinModal({ branding, open, onClose }) {
  if (!open) return null;
  const c = branding?.contact || {};
  const accent = branding?.brand?.accent_color || "#6366F1";
  const photo = assetUrl(c.support_photo_id);

  const rows = [
    c.telegram && { icon: Send, label: "Telegram", value: c.telegram, href: c.telegram.startsWith("http") ? c.telegram : `https://t.me/${c.telegram.replace(/^@/, "")}` },
    c.email && { icon: Mail, label: "Email", value: c.email, href: `mailto:${c.email}` },
    c.instagram && { icon: Instagram, label: "Instagram", value: c.instagram, href: c.instagram.startsWith("http") ? c.instagram : `https://instagram.com/${c.instagram.replace(/^@/, "")}` },
    c.discord && { icon: MessageCircle, label: "Discord", value: c.discord, href: c.discord.startsWith("http") ? c.discord : undefined },
    ...(c.links || []).filter((l) => l?.url).map((l) => ({ icon: Link2, label: l.label || "Link", value: l.label || l.url, href: l.url })),
  ].filter(Boolean);

  const hasAny = photo || c.support_name || rows.length;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" data-testid="join-modal">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-md overflow-hidden rounded-2xl border border-white/[0.1] bg-[#0A0A0A] p-8"
        style={{ boxShadow: `0 0 80px -20px ${accent}55` }}>
        <button onClick={onClose} data-testid="join-modal-close" className="absolute right-4 top-4 text-muted-foreground hover:text-foreground"><X className="h-4 w-4" /></button>
        <div className="flex flex-col items-center text-center">
          {photo ? (
            <img src={photo} alt={c.support_name} className="h-20 w-20 rounded-full object-cover ring-2 ring-white/10" />
          ) : (
            <div className="flex h-20 w-20 items-center justify-center rounded-full text-2xl font-bold" style={{ background: `${accent}22`, color: accent }}>
              {(c.support_name || branding?.brand?.name || "?").charAt(0)}
            </div>
          )}
          <h3 className="mt-4 font-display text-xl font-bold text-foreground">{c.support_name || "Get access"}</h3>
          {c.support_title && <p className="text-sm text-muted-foreground">{c.support_title}</p>}
          <p className="mt-3 max-w-xs text-sm text-muted-foreground">
            {hasAny && rows.length ? "Reach out to request access to the platform." : "Contact details are being set up. Please check back soon."}
          </p>
        </div>
        {rows.length > 0 && (
          <div className="mt-6 space-y-2">
            {rows.map((r, i) => (
              <a key={i} href={r.href} target="_blank" rel="noreferrer" data-testid={`join-contact-${r.label.toLowerCase()}`}
                className="flex items-center gap-3 rounded-xl border border-white/[0.08] bg-[#0F0F0F] px-4 py-3 transition-colors hover:border-white/20">
                <span className="flex h-8 w-8 items-center justify-center rounded-lg" style={{ background: `${accent}1a`, color: accent }}><r.icon className="h-4 w-4" /></span>
                <div className="min-w-0 text-left">
                  <div className="text-[11px] uppercase tracking-wider text-muted-foreground">{r.label}</div>
                  <div className="truncate text-sm text-foreground">{r.value}</div>
                </div>
              </a>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
